import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';

import type {
  BundleCandidateParent,
  BundlePartyKeySource,
  BundlePlanRow,
  BundleRowResult,
  CounterpartyKind,
  SelectableParty,
  UploadBundleExecuteRequest,
  UploadBundleExecuteResponse,
  UploadBundlePlanResponse,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import {
  executeUploadBundle,
  planUploadBundle,
  refusalFieldError,
  sha256OfFile,
} from '../api/upload';
import { loginRequest, msalInstance } from '../auth/msal';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { PartyPicker } from '../components/PartyPicker';
import { SelectionToolbar } from '../components/SelectionToolbar';
import { TagInput } from '../components/TagInput';
import { VendorPicker } from '../components/VendorPicker';
import { BaseDeviationOffer } from './BaseDeviationOffer';
import { classifySowShape, MAX_SOW_NUMBER_LENGTH, SHAPE_REFUSAL } from './ingest/sow-shape';
import { leadingSuggestion, matchPercent } from './party-match';

/**
 * Several documents — CLM-57. One PDF or one ZIP of signed MSAs/SOWs, filed
 * through **plan → review → execute**.
 *
 * ## FR-14 holds here exactly as on the single form
 *
 * Every choice in the review table is a selection over what the plan offered:
 * the client over `parties` (the scoped `selectable-parties` statement), the
 * parent over `candidate_parents_by_party` plus the MSA rows of this very
 * bundle. The one text input is a row's **SOW number** (IR-01 — typed, or
 * accepted from the candidates the server read from the path; never minted),
 * exactly as the single form's: the contract key is never typed, and
 * `AddContractBundle.test.tsx` asserts that the number cells are the only
 * textboxes.
 *
 * ## Nobody picks a type (CLM-118, D-110-3)
 *
 * A bundle is an upload, and nobody filing it is asked for a type (B9): no row
 * select, no ZIP-wide default. The server types each row from its path —
 * folder first (CLM-57), a type word or a SOW-shaped number — and the review
 * shows that type with its evidence. A row whose path gives no type, or names
 * two, files **Type pending** and is not blocked; the AI reads it. The number
 * cell (R-118-19) is required on a SOW row (pre-filled with its SOW-shaped
 * number) and optional on a row with no type (pre-filled with any number the
 * path carries): left filled, the row files as a SOW, never a trusted
 * file-name type; cleared, it files Type pending. Validity reads the server's
 * type the same way: a SOW row, or an untyped row with a number, may sit under
 * a filed MSA or an MSA row of this bundle; an untyped row with none offers
 * what each candidate's `valid_for_untyped` permits; only an MSA row is a
 * parent. Each filed row's CLM-112 offer reads the type the server resolved,
 * from that row's `upload` response.
 *
 * ## The plan is not persisted
 *
 * The same `File` object is posted on plan and on execute (rule 8 — no staging
 * store). The server re-expands it and refuses with `409` if the digest moved;
 * this component then says so and offers to plan again.
 *
 * ## Accessibility (CLM-51)
 *
 * The drop zone is a `div role="button"` **beside** the file input, never
 * wrapping a button; every select and checkbox has a real label or an
 * `aria-label` naming the file; flags and outcomes are text plus a glyph, never
 * colour alone; the results heading takes focus when results land.
 */

/* -------------------------------------------------------------------------- */
/* State                                                                       */
/* -------------------------------------------------------------------------- */

type RelationshipKind = 'standalone' | 'child_of' | 'amends' | 'supersedes';

interface RowEdit {
  readonly include: boolean;
  readonly party: string;
  /**
   * The party the picker handed out for `party`, when it came from a search
   * rather than the plan's `parties` — so the cell renders `display_name (key)`
   * even after DataTable remounts it (CLM-68). `party` alone is the value.
   */
  readonly picked_party: SelectableParty | null;
  /**
   * CLM-78 (B6) — Client or Vendor for this row: swaps the party cell's picker.
   * Seeded from the bundle default (or the detected party's type); never sent
   * on execute — vendor-ness is the party's own `party_type` (K5).
   */
  readonly kind: CounterpartyKind;
  /** CLM-118 (R-118-19) — the number cell, as typed or pre-filled; trimmed and upper-cased when sent. */
  readonly sow_number: string;
  readonly relationship: RelationshipKind;
  /** A contract key, or `row:<row_id>` for an MSA in this bundle. */
  readonly target: string;
  /** CLM-77 — per-row tags, seeded from the default at plan time (D-77-6). */
  readonly tags: readonly string[];
  /**
   * CLM-126 (R-126-11, R-126-17 (1)) — what the plan's own evidence named for
   * this row (`row.party_match`), seeded for **every** row, not only one whose
   * suggestion was accepted — the server would otherwise mis-credit an
   * untouched `sow_prefix` row to the plain default actor the moment this
   * field goes missing for every *other* reason. Reset to `'manual'` by the
   * picker's own `onChange` on any free pick, so "absent" means only one
   * thing on the wire: an older web build (the field is optional there).
   */
  readonly partyKeySource: BundlePartyKeySource | undefined;
  /** CLM-126 (R-126-12) — set only by the quick-pick below; cleared by any free pick. */
  readonly suggestionAccepted: { readonly party_key: string; readonly score: number } | undefined;
}

/**
 * What one approved row was filed as — kept from the request, because a result row does not carry the relationship.
 * The CLM-112 offer reads it with the type from the row's `upload` response: the resolved type (the single form's
 * `accepted` panel does the same).
 */
interface SubmittedRow {
  readonly amends: boolean;
}

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'hashing' }
  | { readonly kind: 'planning' }
  | { readonly kind: 'planned'; readonly plan: UploadBundlePlanResponse }
  | { readonly kind: 'executing'; readonly plan: UploadBundlePlanResponse }
  | {
      readonly kind: 'executed';
      readonly plan: UploadBundlePlanResponse;
      readonly results: UploadBundleExecuteResponse;
      /** By `row_id`, as approved — never the table's current edits. */
      readonly submitted: Readonly<Record<string, SubmittedRow>>;
    }
  | {
      readonly kind: 'refused';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
      readonly field_error: { readonly path: string; readonly message: string } | null;
      /** The plan the refusal interrupted, so the table stays on screen. */
      readonly plan: UploadBundlePlanResponse | null;
    };

const RELATIONSHIP_LABEL: Readonly<Record<RelationshipKind, string>> = {
  standalone: 'Stands alone',
  child_of: 'Sits under',
  amends: 'Amends',
  supersedes: 'Supersedes',
};

/** Plain copy for the whole-bundle guards, keyed by the reason token the API leads with. */
const GUARD_COPY: Readonly<Record<string, string>> = {
  not_pdf_or_zip: 'The file is neither a PDF nor a ZIP.',
  too_many_entries: 'The ZIP declares more entries than a bundle may carry; split it.',
  entry_too_large: 'One document in the ZIP is larger than the document cap.',
  total_too_large: 'The ZIP would inflate to more than the bundle can hold; split it.',
  inflate_ratio: 'One entry compresses far beyond what a PDF does; the ZIP was refused as unsafe.',
  nested_archive: 'The ZIP contains another archive; unpack it first.',
  encrypted_entry: 'The ZIP contains an encrypted entry; remove the password and try again.',
  undecodable_entry: 'One entry in the ZIP could not be read; re-create the archive.',
  unsafe_entry_path: 'One entry path in the ZIP escapes its own folder; re-create the archive.',
  bundle_changed: 'The file changed since it was planned. Plan it again.',
};

const ZIP_TYPES: ReadonlySet<string> = new Set(['application/zip', 'application/x-zip-compressed']);

function isPdfOrZip(file: File): 'application/pdf' | 'application/zip' | null {
  const name = file.name.toLowerCase();
  if (file.type === 'application/pdf' || (file.type === '' && name.endsWith('.pdf'))) return 'application/pdf';
  if (ZIP_TYPES.has(file.type) || (file.type === '' && name.endsWith('.zip'))) return 'application/zip';
  if (name.endsWith('.zip')) return 'application/zip';
  if (name.endsWith('.pdf')) return 'application/pdf';
  return null;
}

/** CLM-118 (R-118-5) — the SOW-shaped candidate shapes: the only numbers that are type evidence. */
const SOW_SHAPED: ReadonlySet<string> = new Set(['dominant_shape', 'sow_infix']);

/**
 * R-118-19 — what a row's number cell starts with: a SOW row's first SOW-shaped
 * candidate, and otherwise nothing. A row the path gives no type starts empty, as
 * on Add contract, so it files as Type pending and the AI decides (D-110-3). Its
 * bare numbers (a year, a version) are suggested in the cell's list, never
 * pre-filled, because a filled cell files the row as a SOW; a SOW-shaped one
 * would already have typed it SOW (R-118-5).
 */
function initialSowNumber(row: BundlePlanRow): string {
  if (row.detected_type !== 'SOW') return '';
  return row.sow_candidates.find((candidate) => candidate.usable_as_lookup_key && SOW_SHAPED.has(candidate.shape))?.value ?? '';
}

function initialEdits(
  plan: UploadBundlePlanResponse,
  defaultTags: readonly string[] = [],
  defaultKind: CounterpartyKind = 'client',
): Record<string, RowEdit> {
  const edits: Record<string, RowEdit> = {};
  for (const row of plan.rows) {
    const duplicate = row.flags.includes('duplicate_in_bundle') || row.flags.includes('duplicate_existing');
    /* CLM-78 — a detected Vendor row is a vendor row whatever the default says. */
    const detected = plan.parties.find((party) => party.party_key === row.detected_party_key);
    edits[row.row_id] = {
      include: !duplicate,
      party: row.detected_party_key ?? '',
      picked_party: null,
      kind: detected === undefined ? defaultKind : detected.party_type === 'Vendor' ? 'vendor' : 'client',
      sow_number: initialSowNumber(row),
      relationship: 'standalone',
      target: '',
      tags: defaultTags,
      /* CLM-126 (R-126-17 (1)) — straight through, not only on an accepted suggestion. */
      partyKeySource: row.party_match ?? undefined,
      suggestionAccepted: undefined,
    };
  }
  return edits;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

export interface UploadBundleModeProps {
  readonly parties: readonly SelectableParty[];
  /** Part 1: the options call failed, so filing is unavailable — controls stay rendered and disabled. */
  readonly unavailable: boolean;
}

export function UploadBundleMode({ parties, unavailable }: UploadBundleModeProps) {
  const [file, setFile] = useState<File | null>(null);
  const [defaultParty, setDefaultParty] = useState('');
  /** CLM-77 (D-77-6) — seeds every row's tags at plan time; the plan request is unchanged. */
  const [defaultTags, setDefaultTags] = useState<readonly string[]>([]);
  /** CLM-78 (B6) — Client or Vendor: filters the offered parties and seeds each row's kind. Never stored. */
  const [defaultKind, setDefaultKind] = useState<CounterpartyKind>('client');
  /** CLM-77 — the toolbar's "Apply tags to selected" popover; appends, never replaces. */
  const [tagging, setTagging] = useState(false);
  const [bulkTags, setBulkTags] = useState<readonly string[]>([]);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [maxBundleBytes, setMaxBundleBytes] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const resultsRef = useRef<HTMLHeadingElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const busy = phase.kind === 'hashing' || phase.kind === 'planning' || phase.kind === 'executing';
  const disabled = unavailable || busy;

  useEffect(() => {
    if (phase.kind !== 'executed') return;
    resultsRef.current?.focus();
  }, [phase]);

  function choose(candidate: File | null): void {
    setPhase({ kind: 'idle' });
    setEdits({});
    if (candidate === null) {
      setFile(null);
      return;
    }
    if (isPdfOrZip(candidate) === null) {
      setFieldError('Only a PDF or a ZIP of PDFs is accepted.');
      setFile(null);
      return;
    }
    if (maxBundleBytes !== null && candidate.size > maxBundleBytes) {
      setFieldError(`The file is larger than the bundle cap (${String(Math.floor(maxBundleBytes / 1_048_576))} MB).`);
      setFile(null);
      return;
    }
    setFieldError(null);
    setFile(candidate);
  }

  function onDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (disabled) return;
    choose(event.dataTransfer.files[0] ?? null);
  }

  function onZoneKey(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      inputRef.current?.click();
    }
  }

  function refuse(error: unknown, plan: UploadBundlePlanResponse | null): void {
    if (error instanceof ApiError && error.isUnauthenticated) {
      void msalInstance.loginRedirect(loginRequest);
      return;
    }
    setPhase({
      kind: 'refused',
      problem: classifyApiError(error),
      detail: error instanceof ApiError ? error.message : null,
      correlation_id: error instanceof ApiError ? error.correlation_id : null,
      field_error: error instanceof ApiError ? refusalFieldError(error.body) : null,
      plan,
    });
  }

  async function runPlan(bundle: File, controller: AbortController): Promise<void> {
    const media = isPdfOrZip(bundle);
    if (media === null) return;
    try {
      setPhase({ kind: 'hashing' });
      const sha256 = await sha256OfFile(bundle);
      if (controller.signal.aborted) return;
      setPhase({ kind: 'planning' });
      const plan = await planUploadBundle(
        {
          bundle: { file_name: bundle.name, media_type: media, size_bytes: bundle.size, sha256 },
          ...(defaultParty === '' ? {} : { default_party_key: defaultParty }),
        },
        bundle,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setMaxBundleBytes(plan.max_bundle_bytes);
      setEdits(initialEdits(plan, defaultTags, defaultKind));
      setPhase({ kind: 'planned', plan });
    } catch (error) {
      if (controller.signal.aborted) return;
      refuse(error, null);
    }
  }

  async function runExecute(
    bundle: File,
    plan: UploadBundlePlanResponse,
    controller: AbortController,
  ): Promise<void> {
    const media = isPdfOrZip(bundle);
    if (media === null) return;
    try {
      setPhase({ kind: 'executing', plan });
      const approved: UploadBundleExecuteRequest['approved'] = [];
      for (const row of plan.rows) {
        const edit = edits[row.row_id];
        if (edit === undefined || !edit.include || edit.party === '') continue;
        approved.push({
          row_id: row.row_id,
          sha256: row.sha256,
          primary_party_key: edit.party,
          /* CLM-118 (B9) — no type: the server re-detects it from the row's path, as the plan showed. */
          relationship:
            edit.relationship === 'standalone'
              ? { relationship: 'standalone' }
              : edit.relationship === 'child_of' && edit.target.startsWith('row:')
                ? { relationship: 'child_of', parent_row_id: edit.target.slice(4) }
                : { relationship: edit.relationship, target_contract_key: edit.target },
          /* Only where the row's SOW-number rule takes one (`sowRuleOf`); on a row with no type, a number files it as a SOW (R-118-19). */
          ...(sowNumberSent(row, edit) ? { sow_number: edit.sow_number.trim().toUpperCase() } : {}),
          ...(edit.tags.length > 0 ? { tags: [...edit.tags] } : {}),
          /* CLM-126 (R-126-17 (1)) — sent for every row, not only one whose
           * suggestion was accepted: an untouched `sow_prefix`/`folder`/
           * `file_name`/`default` row still needs its own evidence named, or
           * the server has no way to tell it apart from a free pick. The
           * server re-verifies this claim against its own re-detection
           * (R-126-17 (2)) — it is never trusted outright. */
          ...(edit.partyKeySource === undefined ? {} : { party_key_source: edit.partyKeySource }),
          ...(edit.suggestionAccepted === undefined ? {} : { suggestion_accepted: edit.suggestionAccepted }),
        });
      }
      const results = await executeUploadBundle(
        {
          bundle: { file_name: bundle.name, media_type: media, size_bytes: bundle.size, sha256: plan.bundle_sha256 },
          bundle_sha256: plan.bundle_sha256,
          approved,
        },
        bundle,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      const submitted: Record<string, SubmittedRow> = {};
      for (const row of approved) {
        submitted[row.row_id] = { amends: row.relationship.relationship === 'amends' };
      }
      setPhase({ kind: 'executed', plan, results, submitted });
    } catch (error) {
      if (controller.signal.aborted) return;
      refuse(error, plan);
    }
  }

  function start(work: (controller: AbortController) => Promise<void>): void {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void work(controller);
  }

  const plan =
    phase.kind === 'planned' || phase.kind === 'executing' || phase.kind === 'executed'
      ? phase.plan
      : phase.kind === 'refused'
        ? phase.plan
        : null;

  /* ---- per-row derived facts ------------------------------------------ */

  function editOf(row: BundlePlanRow): RowEdit {
    return (
      edits[row.row_id] ?? {
        include: false,
        party: row.detected_party_key ?? '',
        picked_party: null,
        kind: 'client',
        sow_number: '',
        relationship: 'standalone',
        target: '',
        tags: [],
        partyKeySource: row.party_match ?? undefined,
        suggestionAccepted: undefined,
      }
    );
  }

  function update(rowId: string, patch: Partial<RowEdit>): void {
    setEdits((current) => {
      const previous = current[rowId];
      if (previous === undefined) return current;
      return { ...current, [rowId]: { ...previous, ...patch } };
    });
  }

  /**
   * Select all / Clear for the Include column (CLM-69, D-69-5/D-69-6). The
   * selection stays `edits[row_id].include`; Select all takes every plan row —
   * duplicate-flagged ones included, the click being the explicit human act
   * (the flag stays visible as text and the server refuses a true duplicate).
   */
  function setIncludeAll(on: boolean): void {
    setEdits((current) => {
      const next: Record<string, RowEdit> = {};
      for (const [rowId, edit] of Object.entries(current)) next[rowId] = { ...edit, include: on };
      return next;
    });
  }

  /** CLM-77 — append the popover's tags to every included row, deduplicated; rows keep what they had. */
  function applyBulkTags(): void {
    if (bulkTags.length === 0) return;
    setEdits((current) => {
      const next: Record<string, RowEdit> = {};
      for (const [rowId, edit] of Object.entries(current)) {
        next[rowId] = edit.include ? { ...edit, tags: [...new Set([...edit.tags, ...bulkTags])] } : edit;
      }
      return next;
    });
    setBulkTags([]);
    setTagging(false);
  }

  /**
   * MSA rows of this plan for the same client, offered as in-bundle parents: rows the server types MSA
   * from their path. Never a row that amends: it takes its agreement's type, so the server would refuse
   * it as a parent, and the filed list never offers an amendment either (`candidateParentsQuery`).
   */
  function bundleMsaRows(row: BundlePlanRow, edit: RowEdit): readonly BundlePlanRow[] {
    if (plan === null || edit.party === '') return [];
    return plan.rows.filter((other) => {
      if (other.row_id === row.row_id) return false;
      const otherEdit = editOf(other);
      return otherEdit.include && other.detected_type === 'MSA' && otherEdit.relationship !== 'amends' && otherEdit.party === edit.party;
    });
  }

  function filedParents(edit: RowEdit): readonly BundleCandidateParent[] {
    if (plan === null || edit.party === '') return [];
    return plan.candidate_parents_by_party[edit.party] ?? [];
  }

  /**
   * The type the server files this row under (CLM-118): its `detected_type` when the path gives one;
   * with none, a number in the cell files it as a SOW (R-118-19) — never on a row whose name names two
   * types, which takes no number — and otherwise it is Type pending (`null`).
   */
  function filedTypeOf(row: BundlePlanRow, edit: RowEdit): BundlePlanRow['detected_type'] {
    if (row.detected_type !== null) return row.detected_type;
    return row.name_evidence === 'untyped' && edit.sow_number.trim() !== '' ? 'SOW' : null;
  }

  /**
   * The relationships a filed candidate permits for this row: by the type its path gives the server; for
   * a row with none, every one the candidate's own type and lifecycle permit (`valid_for_untyped` — an
   * `amends` or `supersedes` takes the target's type) and, when a number in its cell makes it a SOW
   * (R-118-19), what a SOW may do too — superseding a SOW needs the number a Type pending row lacks.
   */
  function validFor(candidate: BundleCandidateParent, row: BundlePlanRow, edit: RowEdit): readonly string[] {
    if (row.detected_type !== null) return candidate.valid_for[row.detected_type];
    return filedTypeOf(row, edit) === 'SOW'
      ? [...new Set([...candidate.valid_for_untyped, ...candidate.valid_for.SOW])]
      : candidate.valid_for_untyped;
  }

  /**
   * Whether the server files this row as an MSA: only when its path gives MSA (`detected_type`, the
   * server's own folder-first detection). Such a row may not sit `child_of` anything; a row with no
   * type is a SOW with a number and Type pending without, never an MSA.
   */
  function resolvesToMsa(row: BundlePlanRow): boolean {
    return row.detected_type === 'MSA';
  }

  function relationshipsFor(row: BundlePlanRow, edit: RowEdit): readonly RelationshipKind[] {
    const out: RelationshipKind[] = ['standalone'];
    if (edit.party === '') return out;
    const fromFiled = new Set(filedParents(edit).flatMap((candidate) => validFor(candidate, row, edit)));
    /* A row the server files as anything but an MSA may sit under a filed MSA or an MSA row of this bundle. */
    if (!resolvesToMsa(row) && (fromFiled.has('child_of') || bundleMsaRows(row, edit).length > 0)) {
      out.push('child_of');
    }
    if (fromFiled.has('amends')) out.push('amends');
    if (fromFiled.has('supersedes')) out.push('supersedes');
    return out;
  }

  /** The MSA rows of this bundle the Parent select offers: for `child_of` only. */
  function bundleTargetsFor(row: BundlePlanRow, edit: RowEdit): readonly BundlePlanRow[] {
    return edit.relationship === 'child_of' ? bundleMsaRows(row, edit) : [];
  }

  /** The filed contracts the Parent select offers for the row's current relationship. */
  function filedTargetsFor(row: BundlePlanRow, edit: RowEdit): readonly BundleCandidateParent[] {
    if (edit.relationship === 'standalone') return [];
    return filedParents(edit).filter((candidate) => validFor(candidate, row, edit).includes(edit.relationship));
  }

  /**
   * Whether the row's relationship and target are still on offer. A change made after they were
   * chosen can withdraw them: a number cleared on a row with no type (it is Type pending again), or an
   * in-bundle parent unticked or made an amendment. Such a row is not approvable until the reviewer
   * chooses again, so Execute never sends a pair the server refuses (`relationship_not_valid`, or the
   * in-bundle parent check).
   */
  function relationshipOffered(row: BundlePlanRow, edit: RowEdit): boolean {
    if (edit.relationship === 'standalone') return true;
    if (!relationshipsFor(row, edit).includes(edit.relationship)) return false;
    return targetOffered(row, edit);
  }

  /** Whether the row's chosen target is still one the Parent select offers for its relationship. */
  function targetOffered(row: BundlePlanRow, edit: RowEdit): boolean {
    return (
      bundleTargetsFor(row, edit).some((other) => `row:${other.row_id}` === edit.target) ||
      filedTargetsFor(row, edit).some((candidate) => candidate.contract_key === edit.target)
    );
  }

  /**
   * A new number in a row's cell (R-118-19). On a row with no type it changes what the row files as — a
   * SOW, or Type pending when cleared — so a relationship or target it no longer offers resets the row to
   * standalone, as the party select does.
   */
  function withSowNumber(row: BundlePlanRow, edit: RowEdit, sowNumber: string): Partial<RowEdit> {
    const next = { ...edit, sow_number: sowNumber };
    const kept =
      edit.relationship === 'standalone' ||
      (relationshipsFor(row, next).includes(edit.relationship) && (edit.target === '' || targetOffered(row, next)));
    return kept ? { sow_number: sowNumber } : { sow_number: sowNumber, relationship: 'standalone', target: '' };
  }

  /** The filed contract this row names as its target, when it names one. */
  function filedTarget(edit: RowEdit): BundleCandidateParent | null {
    if (edit.target === '' || edit.target.startsWith('row:')) return null;
    return filedParents(edit).find((candidate) => candidate.contract_key === edit.target) ?? null;
  }

  /**
   * The row's SOW-number rule, from the server's type, its relationship and its filed target — what the
   * single upload's `sowField` derives (R-118-19), so the review never approves a row the server must
   * refuse:
   *
   *  - `amends` — `hidden`: the amendment takes its agreement's number, or none; nothing is sent;
   *  - `supersedes` of a SOW — `required`: the replacement is a SOW and needs its number;
   *  - `supersedes` of anything else — `hidden`: the replacement takes that type, which holds no number;
   *  - a SOW row — `required` (D-71-0; execute refuses a missing one, `sow_number_required`);
   *  - a row the path gives no type — `optional`: a number files it as a SOW, none leaves it Type pending;
   *  - an MSA or NDA row, or one whose name names two types — `none`: a number there is refused
   *    (`sow_number_conflicts_with_name`).
   */
  function sowRuleOf(row: BundlePlanRow, edit: RowEdit): 'required' | 'optional' | 'hidden' | 'none' {
    if (edit.relationship === 'amends') return 'hidden';
    if (edit.relationship === 'supersedes') return filedTarget(edit)?.contract_type === 'SOW' ? 'required' : 'hidden';
    if (row.detected_type === 'SOW') return 'required';
    if (row.detected_type === null && row.name_evidence === 'untyped') return 'optional';
    return 'none';
  }

  function sowNumberSent(row: BundlePlanRow, edit: RowEdit): boolean {
    const rule = sowRuleOf(row, edit);
    return (rule === 'required' || rule === 'optional') && edit.sow_number.trim() !== '';
  }

  /** Why a typed number is refused before it is sent — the single form's classifier (IR-01); `null` when usable or empty. */
  function sowNumberProblem(edit: RowEdit): string | null {
    if (edit.sow_number.trim() === '') return null;
    const verdict = classifySowShape(edit.sow_number);
    return verdict.usable ? null : SHAPE_REFUSAL[verdict.shape];
  }

  function approvable(row: BundlePlanRow): boolean {
    const edit = editOf(row);
    /* CLM-118 (B9) — no type is needed: the server types the row from its path, or files it Type pending. */
    if (!edit.include || edit.party === '') return false;
    /* D-71-0 — a SOW is never filed without its number; execute refuses one per row (`sow_number_required`). */
    if (sowRuleOf(row, edit) === 'required' && edit.sow_number.trim() === '') return false;
    if (sowNumberSent(row, edit) && sowNumberProblem(edit) !== null) return false;
    return relationshipOffered(row, edit);
  }

  const includedRows = plan === null ? [] : plan.rows.filter((row) => editOf(row).include);
  const canExecute =
    !disabled && file !== null && phase.kind === 'planned' && includedRows.length > 0 && includedRows.every(approvable);

  /* ---- columns ---------------------------------------------------------- */

  const columns: readonly DataTableColumn<BundlePlanRow>[] = [
    {
      id: 'file',
      header: 'File',
      cell: (row) => (
        <>
          <span className="bundle-path">{row.path}</span>
          <span className="mut"> {Math.max(1, Math.round(row.size_bytes / 1024))} KB</span>
        </>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (row) => {
        const edit = editOf(row);
        /* CLM-118 — the server's type, never a choice: an amendment or replacement follows its target. */
        if (edit.relationship === 'amends' || edit.relationship === 'supersedes') {
          return <span className="mut">follows the related contract</span>;
        }
        const type = filedTypeOf(row, edit);
        return type === null ? (
          <ContractTypeTag type="OTHER" confirmation="pending" />
        ) : (
          <ContractTypeTag type={type} confirmation="provisional" />
        );
      },
    },
    {
      id: 'type_match',
      header: 'Type from',
      cell: (row) =>
        row.name_evidence === 'conflicting' ? (
          /* CLM-118 (R-118-5) — the path names more than one type: no trusted type, so Type pending. */
          <span className="tag out" title="the name names more than one type, or a type and a SOW number">
            conflicting names
          </span>
        ) : row.type_match === 'sow_number' ? (
          /* CLM-76 (D-76-3): no MSA / NDA / SOW token — the SOW-shaped number is the only evidence. */
          <span className="tag ai" title="type inferred from the SOW number in the name — no MSA / NDA / SOW token">
            inferred
          </span>
        ) : row.type_match === null ? (
          editOf(row).sow_number.trim() !== '' && sowRuleOf(row, editOf(row)) === 'optional' ? (
            <span className="mut">the number you entered</span>
          ) : (
            <span className="mut">— the AI reads it</span>
          )
        ) : (
          <span className="mut">{row.type_match === 'folder' ? 'folder' : 'file name'}</span>
        ),
    },
    {
      id: 'party',
      header: 'Counterparty',
      cell: (row) => {
        const edit = editOf(row);
        /* CLM-68 — the plan's `parties` carries every detected party, so a
         * detected key renders without a request; a searched pick is kept on
         * the row edit so a remounted cell still renders it; a filed row is
         * read-only text. */
        const known = plan?.parties ?? parties;
        /* CLM-126 (R-126-13) — the row's own nearest-match candidates, ahead of
         * the plan's ordinary list: seeds the picker so an accepted suggestion
         * (below) renders its name without a search. */
        const base = edit.picked_party === null ? known : [edit.picked_party, ...known];
        const pickerProps = {
          id: `upload-bundle-party-${row.row_id}`,
          value: edit.party,
          disabled,
          readOnly: phase.kind !== 'planned',
          initialParties: [...row.nearest_matches, ...base],
          onChange: (party: SelectableParty | null) =>
            update(row.row_id, {
              party: party?.party_key ?? '',
              picked_party: party,
              relationship: 'standalone',
              target: '',
              /* CLM-126 (R-126-17 (1)) — a free pick is always 'manual', never a
               * stale suggestion; clearing the party clears the source too. */
              partyKeySource: party === null ? undefined : 'manual',
              suggestionAccepted: undefined,
            }),
        };
        const suggestion = phase.kind === 'planned' ? leadingSuggestion(row.nearest_matches) : null;
        return (
          <>
            {phase.kind === 'planned' ? (
              /* CLM-78 (B6) — Client or Vendor swaps the picker; the row's kind is never sent (K5). */
              <select
                aria-label={`Counterparty kind of ${row.file_name}`}
                className="bundle-kind"
                value={edit.kind}
                disabled={disabled}
                onChange={(event) =>
                  update(row.row_id, {
                    kind: event.target.value as CounterpartyKind,
                    party: '',
                    picked_party: null,
                    relationship: 'standalone',
                    target: '',
                    partyKeySource: undefined,
                    suggestionAccepted: undefined,
                  })
                }
              >
                <option value="client">Client</option>
                <option value="vendor">Vendor</option>
              </select>
            ) : null}
            {edit.kind === 'vendor' ? (
              <VendorPicker {...pickerProps} label={`Vendor of ${row.file_name}`} emptyLabel="Choose a vendor…" />
            ) : (
              <PartyPicker {...pickerProps} label={`Client of ${row.file_name}`} emptyLabel="Choose a client…" />
            )}
            {/* CLM-126 (R-126-13 (2)) — the one-click suggestion; a free pick
             * through the picker above, never a separate code path. */}
            {suggestion === null ? null : (
              <span className="fl quickpicks">
                <button
                  type="button"
                  className="btn sm pri"
                  disabled={disabled}
                  onClick={() =>
                    update(row.row_id, {
                      party: suggestion.party_key,
                      picked_party: suggestion,
                      relationship: 'standalone',
                      target: '',
                      partyKeySource: 'nearest_match',
                      suggestionAccepted: { party_key: suggestion.party_key, score: suggestion.suggestion_score ?? 0 },
                    })
                  }
                >
                  Use {suggestion.display_name} ({matchPercent(suggestion.suggestion_score ?? 0)})
                </button>
              </span>
            )}
          </>
        );
      },
    },
    {
      id: 'sow',
      header: 'SOW number',
      cell: (row) => {
        const edit = editOf(row);
        const candidates = row.sow_candidates.filter((candidate) => candidate.usable_as_lookup_key);
        /*
         * `sowRuleOf` decides (R-118-19): an amendment takes its agreement's number and a replacement of a
         * non-SOW holds none (—), as do an MSA or NDA row and one whose name names two types; a SOW, or a
         * replacement of one, needs one; a row with no type may carry one, which files it as a SOW.
         */
        const rule = sowRuleOf(row, edit);
        if (rule === 'hidden' || rule === 'none') return <span className="mut">—</span>;
        if (phase.kind !== 'planned') return edit.sow_number.trim() === '' ? <span className="mut">—</span> : edit.sow_number;
        const problem = sowNumberProblem(edit);
        const missing = rule === 'required' && edit.sow_number.trim() === '';
        const listId = `upload-bundle-sow-candidates-${row.row_id}`;
        return (
          <>
            <input
              type="text"
              aria-label={`SOW number of ${row.file_name}`}
              value={edit.sow_number}
              disabled={disabled}
              required={rule === 'required'}
              aria-required={rule === 'required' ? 'true' : 'false'}
              aria-invalid={missing || problem !== null ? true : undefined}
              maxLength={MAX_SOW_NUMBER_LENGTH}
              placeholder={rule === 'required' ? 'Required — e.g. AGESOW25264OV' : 'Optional'}
              {...(candidates.length === 0 ? {} : { list: listId })}
              onChange={(event) => update(row.row_id, withSowNumber(row, edit, event.target.value))}
            />
            {candidates.length === 0 ? null : (
              <datalist id={listId}>
                {candidates.map((candidate) => (
                  <option key={candidate.value} value={candidate.value}>
                    {candidate.shape.replace(/_/g, ' ')}
                  </option>
                ))}
              </datalist>
            )}
            {problem === null ? null : (
              <span className="ferr" role="status">
                Not a usable SOW number: {problem}.
              </span>
            )}
            {rule === 'optional' ? (
              <span className="mut">
                {edit.sow_number.trim() === '' ? ' Files as Type pending.' : ' Files it as a SOW.'}
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: (row) => {
        const edit = editOf(row);
        if (phase.kind !== 'planned') {
          return edit.tags.length === 0 ? (
            <span className="mut">—</span>
          ) : (
            <ul className="tags-ro" aria-label={`Tags of ${row.file_name}`}>
              {edit.tags.map((tag) => (
                <li key={tag} className="tag">
                  {tag}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <TagInput
            id={`upload-bundle-tags-${row.row_id}`}
            label={`Tags of ${row.file_name}`}
            value={edit.tags}
            disabled={disabled}
            onChange={(tags) => update(row.row_id, { tags })}
          />
        );
      },
    },
    {
      id: 'relationship',
      header: 'Relationship',
      cell: (row) => {
        const edit = editOf(row);
        const kinds = relationshipsFor(row, edit);
        const filed = filedTargetsFor(row, edit);
        const inBundle = bundleTargetsFor(row, edit);
        return (
          <>
            <select
              aria-label={`Relationship of ${row.file_name}`}
              value={edit.relationship}
              disabled={disabled || phase.kind !== 'planned'}
              onChange={(event) => update(row.row_id, { relationship: event.target.value as RelationshipKind, target: '' })}
            >
              {kinds.map((kind) => (
                <option key={kind} value={kind}>
                  {RELATIONSHIP_LABEL[kind]}
                </option>
              ))}
            </select>
            {edit.relationship !== 'standalone' ? (
              <select
                aria-label={`Parent of ${row.file_name}`}
                value={edit.target}
                disabled={disabled || phase.kind !== 'planned'}
                onChange={(event) => update(row.row_id, { target: event.target.value })}
              >
                <option value="">Choose a contract…</option>
                {inBundle.map((other) => (
                  <option key={other.row_id} value={`row:${other.row_id}`}>
                    MSA in this bundle (row {other.row_id.slice(1)}) · {other.file_name}
                  </option>
                ))}
                {filed.map((candidate) => (
                  <option key={candidate.contract_key} value={candidate.contract_key}>
                    {candidate.display_label}
                  </option>
                ))}
              </select>
            ) : null}
          </>
        );
      },
    },
    {
      id: 'flags',
      header: 'Flags',
      cell: (row) => <RowFlags row={row} />,
    },
    {
      id: 'include',
      header: 'Include',
      cell: (row) => {
        const edit = editOf(row);
        return (
          <input
            type="checkbox"
            aria-label={`Include ${row.file_name}`}
            checked={edit.include}
            disabled={disabled || phase.kind !== 'planned'}
            onChange={(event) => update(row.row_id, { include: event.target.checked })}
          />
        );
      },
    },
  ];

  /* ---- render ------------------------------------------------------------ */

  return (
    <div className="card upload-form bundle" aria-busy={busy}>
      <h3>File several signed contracts</h3>
      <p>
        Choose one PDF, or one ZIP of PDFs — an MSA and its SOWs, several clients, any mix. The API
        reads each file name and folder for the client, the type and the SOW number, shows you what
        it found, and files only the rows you approve. Nobody picks a type, and every key is minted
        by the API; the only thing you may type is a SOW number the path does not carry.
      </p>

      <div className="frow">
        <div className="field grow">
          <label htmlFor="upload-bundle">Bundle (PDF or ZIP)</label>
          <input
            id="upload-bundle"
            ref={inputRef}
            type="file"
            accept="application/pdf,application/zip,.zip,.pdf"
            disabled={disabled}
            aria-describedby="upload-bundle-accepts"
            onChange={(event) => choose(event.target.files?.[0] ?? null)}
          />
          <div
            className="bundle-drop"
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            aria-describedby="upload-bundle-accepts"
            onClick={() => {
              if (!disabled) inputRef.current?.click();
            }}
            onKeyDown={onZoneKey}
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
          >
            {file === null ? 'Drop a PDF or ZIP here, or press Enter to choose one' : `Chosen: ${file.name}`}
          </div>
          <p id="upload-bundle-accepts" className="mut">
            One PDF or one ZIP of PDFs.
            {maxBundleBytes !== null ? ` Up to ${String(Math.floor(maxBundleBytes / 1_048_576))} MB per bundle.` : ''}
            {' '}Entries that are not PDFs are listed and never filed.
          </p>
        </div>
      </div>

      <div className="frow">
        <div className="field">
          <label htmlFor="upload-bundle-default-kind">Default counterparty kind</label>
          {/* CLM-78 (B6) — filters the offered parties and seeds each row; request-time only, never stored. */}
          <select
            id="upload-bundle-default-kind"
            value={defaultKind}
            disabled={disabled}
            onChange={(event) => {
              setDefaultKind(event.target.value as CounterpartyKind);
              setDefaultParty('');
            }}
          >
            <option value="client">Client</option>
            <option value="vendor">Vendor</option>
          </select>
        </div>
        <div className="field grow">
          <label htmlFor="upload-bundle-default-party">{defaultKind === 'vendor' ? 'Default vendor' : 'Default client'}</label>
          {defaultKind === 'vendor' ? (
            <VendorPicker
              id="upload-bundle-default-party"
              value={defaultParty}
              disabled={disabled}
              initialParties={parties}
              emptyLabel="None — detect from folders and file names"
              onChange={(party) => setDefaultParty(party?.party_key ?? '')}
            />
          ) : (
            <PartyPicker
              id="upload-bundle-default-party"
              value={defaultParty}
              disabled={disabled}
              initialParties={parties}
              emptyLabel="None — detect from folders and file names"
              onChange={(party) => setDefaultParty(party?.party_key ?? '')}
            />
          )}
        </div>
        <div className="field grow">
          <label htmlFor="upload-bundle-default-tags">Default tags</label>
          {/* CLM-77 (D-77-6) — seeds each row at plan time; edit per row afterwards. */}
          <TagInput id="upload-bundle-default-tags" value={defaultTags} onChange={setDefaultTags} disabled={disabled} />
        </div>
      </div>

      {fieldError !== null ? <p className="ferr">{fieldError}</p> : null}

      <div className="bundle-actions">
        <button
          className="btn"
          type="button"
          disabled={disabled || file === null}
          onClick={() => {
            if (file !== null) start((controller) => runPlan(file, controller));
          }}
        >
          {phase.kind === 'hashing' ? 'Reading the bundle…' : phase.kind === 'planning' ? 'Planning…' : 'Plan this bundle'}
        </button>
        {plan !== null && phase.kind !== 'planned' && file !== null ? (
          <button
            className="btn qt"
            type="button"
            disabled={disabled}
            onClick={() => start((controller) => runPlan(file, controller))}
          >
            Re-plan remaining
          </button>
        ) : null}
      </div>

      {busy ? (
        <p role="status" aria-busy="true" className="mut">
          {phase.kind === 'executing'
            ? `Filing ${String(includedRows.length)} documents…`
            : 'Reading the bundle…'}
        </p>
      ) : null}

      {phase.kind === 'refused' ? <Refusal phase={phase} /> : null}

      {plan !== null ? (
        <>
          <div className="totals">
            <Total label="Ready" value={plan.totals.ready} />
            <Total label="Needs client" value={plan.totals.needs_party} tone={plan.totals.needs_party > 0 ? 'bad' : undefined} />
            {/* CLM-118 — informational, never blocking: such a row files Type pending, and the AI reads it. */}
            <Total label="Type pending" value={plan.totals.needs_type} />
            <Total label="Names conflict" value={plan.totals.type_evidence_conflicting} />
            {/* R-118-19 — never blocking in the plan: the number is typed in the row's cell. */}
            <Total label="Needs SOW number" value={plan.totals.needs_sow_number} tone={plan.totals.needs_sow_number > 0 ? 'bad' : undefined} />
            <Total label="Duplicates" value={plan.totals.duplicate_in_bundle + plan.totals.duplicate_existing} />
            <Total label="Restricted" value={plan.totals.restricted} />
            <Total label="Excluded" value={plan.totals.excluded} />
          </div>
          <p className="mut" data-testid="bundle-type-provisional">
            Nobody picks a type: each row’s type comes from its folder or file name. A row whose path gives no type,
            or names more than one, files as Type pending, and the AI reads it. A type stays provisional until the AI
            agrees with the file name or an administrator confirms it.
          </p>
          {plan.exceeds_design_volume ? (
            <p className="mut">
              This bundle holds more than ten documents — above the volume CLM is sized for
              (~10 contracts a month). It will file, but expect it to take a moment.
            </p>
          ) : null}
          {plan.totals.needs_party > 0 ? (
            <p className="mut">
              A client is offered only if you may already file for it. The first contract of a
              brand-new client cannot be filed from here yet.
            </p>
          ) : null}

          {plan.rows.length === 0 ? (
            <p className="upload-restricted" role="note">
              <strong>The bundle contains no PDF documents.</strong> Only PDFs are filed; the
              entries below were listed and skipped.
            </p>
          ) : (
            <>
              <SelectionToolbar
                label="Documents to file"
                noun="documents"
                count={includedRows.length}
                total={plan.rows.length}
                disabled={disabled || phase.kind !== 'planned'}
                onSelectAll={() => setIncludeAll(true)}
                onClear={() => setIncludeAll(false)}
              >
                <button
                  type="button"
                  className="btn sm"
                  disabled={disabled || phase.kind !== 'planned' || includedRows.length === 0}
                  aria-expanded={tagging}
                  aria-controls="upload-bundle-bulk-tags"
                  onClick={() => setTagging((open) => !open)}
                >
                  Apply tags to selected…
                </button>
              </SelectionToolbar>
              {tagging ? (
                <div id="upload-bundle-bulk-tags" className="card secondary tags-popover" role="group" aria-label="Apply tags to selected">
                  <p className="mut">
                    Added to the {includedRows.length.toLocaleString()} included{' '}
                    {includedRows.length === 1 ? 'row' : 'rows'} — appended to any a row already carries, never replacing them.
                  </p>
                  <TagInput id="upload-bundle-bulk-tag-input" label="Tags to apply" value={bulkTags} onChange={setBulkTags} disabled={disabled} />
                  <span className="fl">
                    <button type="button" className="btn sm pri" disabled={disabled || bulkTags.length === 0} onClick={applyBulkTags}>
                      Apply to {includedRows.length.toLocaleString()} selected
                    </button>
                    <button
                      type="button"
                      className="btn sm qt"
                      disabled={disabled}
                      onClick={() => {
                        setBulkTags([]);
                        setTagging(false);
                      }}
                    >
                      Cancel
                    </button>
                  </span>
                </div>
              ) : null}
              <DataTable
                caption="Documents in this bundle"
                columns={columns}
                rows={plan.rows}
                rowKey={(row) => row.row_id}
                busy={phase.kind === 'executing'}
                empty={<p>No documents.</p>}
              />
            </>
          )}

          {plan.excluded.length > 0 ? (
            <details className="bundle-excluded">
              <summary>
                {plan.excluded.length} {plan.excluded.length === 1 ? 'entry' : 'entries'} not filed
              </summary>
              <ul>
                {plan.excluded.map((entry) => (
                  <li key={entry.path}>
                    <span className="bundle-path">{entry.path}</span>{' '}
                    <span className="mut">— {EXCLUSION_COPY[entry.reason]}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          {phase.kind === 'planned' || phase.kind === 'executing' ? (
            <button
              className="btn pri"
              type="button"
              disabled={!canExecute}
              onClick={() => {
                if (file !== null && phase.kind === 'planned') {
                  start((controller) => runExecute(file, phase.plan, controller));
                }
              }}
            >
              {phase.kind === 'executing'
                ? 'Filing…'
                : `File ${String(includedRows.length)} ${includedRows.length === 1 ? 'contract' : 'contracts'}`}
            </button>
          ) : null}
        </>
      ) : null}

      {phase.kind === 'executed' ? (
        <section className="bundle-results" aria-live="polite">
          <h4 ref={resultsRef} tabIndex={-1}>
            Filing results
          </h4>
          <p className="mut">
            {phase.results.totals.uploaded} filed · {phase.results.totals.duplicate} already filed ·{' '}
            {phase.results.totals.conflict} conflicts · {phase.results.totals.rejected} rejected ·{' '}
            {phase.results.totals.failed} failed · {phase.results.totals.skipped} skipped
          </p>
          <DataTable
            caption="Filing results"
            columns={resultColumns(phase.plan, phase.submitted)}
            rows={phase.results.results}
            rowKey={(result) => result.row_id}
            empty={<p>No rows were approved.</p>}
          />
        </section>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Pieces                                                                     */
/* -------------------------------------------------------------------------- */

const EXCLUSION_COPY: Readonly<Record<BundlePlanRow['flags'][number] | 'not_pdf' | 'directory' | 'system_file', string>> = {
  not_pdf: 'not a PDF',
  directory: 'a folder',
  system_file: 'a system file',
  duplicate_in_bundle: 'duplicate in this bundle',
  duplicate_existing: 'already filed',
  needs_party: 'needs a client',
  ambiguous_party: 'more than one party has this name',
  needs_type: 'no type in the name',
  needs_sow_number: 'needs a SOW number',
  restricted: 'restricted',
  type_evidence_conflicting: 'the name names more than one type',
};

function Total({ label, value, tone }: { readonly label: string; readonly value: number; readonly tone?: 'bad' | undefined }) {
  return (
    <div className="total" {...(tone === undefined ? {} : { 'data-tone': tone })}>
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

function RowFlags({ row }: { readonly row: BundlePlanRow }) {
  if (row.flags.length === 0) return <span className="mut">—</span>;
  return (
    <ul className="bundle-flags">
      {row.flags.map((flag) => (
        <li key={flag}>
          <span aria-hidden="true">{flag === 'restricted' ? '◐' : '⚠'}</span>{' '}
          {flag === 'duplicate_in_bundle'
            ? `Duplicate of row ${row.duplicate_of_row_id?.slice(1) ?? '?'}`
            : flag === 'duplicate_existing'
              ? row.existing_contract_key === null
                ? 'Already filed (contract not visible to you)'
                : `Already filed as ${row.existing_contract_key}`
              : flag === 'needs_party'
                ? 'Needs a client'
                : flag === 'ambiguous_party'
                  ? /* CLM-114 (R-114-4) — two parties under one name (display name or party alias): never guessed. */
                    'More than one party has this name — choose one'
                  : flag === 'needs_type'
                    ? 'No type in the name — files as Type pending; the AI reads it, unless you enter a SOW number'
                    : flag === 'needs_sow_number'
                      ? 'No SOW number could be read from the file or folder name. Type it in the SOW number cell — a SOW is never filed without one (D-71-0).'
                      : flag === 'type_evidence_conflicting'
                        ? 'The name names more than one type, or a type and a SOW number — files as Type pending; the AI decides'
                        : 'Restricted — facts entered by hand (§4C)'}
        </li>
      ))}
    </ul>
  );
}

function resultColumns(
  plan: UploadBundlePlanResponse,
  submitted: Readonly<Record<string, SubmittedRow>>,
): readonly DataTableColumn<BundleRowResult>[] {
  const pathOf = (rowId: string): string => plan.rows.find((row) => row.row_id === rowId)?.path ?? rowId;
  return [
    { id: 'file', header: 'File', cell: (result) => <span className="bundle-path">{pathOf(result.row_id)}</span> },
    {
      id: 'outcome',
      header: 'Outcome',
      cell: (result) => <Outcome result={result} submitted={submitted[result.row_id] ?? null} />,
    },
  ];
}

function Outcome({ result, submitted }: { readonly result: BundleRowResult; readonly submitted: SubmittedRow | null }) {
  switch (result.outcome) {
    case 'uploaded':
      return (
        <>
          <span aria-hidden="true">✓</span> Filed as <code>{result.upload?.contract_key}</code>
          {result.upload === undefined ? null : (
            <>
              {' '}
              ·{' '}
              <ContractTypeTag type={result.upload.contract_type} confirmation={result.upload.contract_type_confirmation} />
            </>
          )}
          <span className="mut"> · audit {result.upload?.audit.audit_id}</span>
          {result.upload?.processing_status === 'manual_entry_pending' ? (
            <span className="mut"> · processing-restricted: facts entered by hand</span>
          ) : null}
          {/* CLM-112 (R-112-16) — a filed MSA that amends nothing is offered the deviation report, from its record. */}
          {result.upload === undefined || submitted === null ? null : (
            <BaseDeviationOffer
              contractKey={result.upload.contract_key}
              contractType={result.upload.contract_type}
              isAmendment={submitted.amends}
            />
          )}
        </>
      );
    case 'duplicate':
      return (
        <>
          <span aria-hidden="true">≡</span>{' '}
          {typeof result.contract_key === 'string'
            ? <>Already filed as <code>{result.contract_key}</code></>
            : 'Already filed, not visible to you'}
        </>
      );
    case 'conflict':
      return (
        <>
          {/* `sequence_number_stale` alone means re-plan; a `key_collision` would be offered the same number again (R-110-10). */}
          <span aria-hidden="true">⟳</span> Conflict — {result.reason === 'sequence_number_stale' ? 'sequence moved, re-plan' : result.detail}
        </>
      );
    case 'rejected':
      return (
        <>
          <span aria-hidden="true">✕</span> Rejected — {result.detail ?? result.reason}
        </>
      );
    case 'skipped_parent_not_filed':
      return (
        <>
          <span aria-hidden="true">↷</span> Skipped — parent not filed
        </>
      );
    case 'failed':
    default:
      return (
        <>
          <span aria-hidden="true">✕</span> Failed — {result.detail ?? result.reason}
        </>
      );
  }
}

function Refusal({ phase }: { readonly phase: Extract<Phase, { kind: 'refused' }> }) {
  if (phase.problem === 'not_implemented') {
    return (
      <p className="upload-restricted" role="note">
        <strong>Not configured.</strong> Guided upload is not switched on for this environment
        yet: the API has no SharePoint intake drive configured, or the write grant on its
        managed identity is still outstanding.
      </p>
    );
  }
  const token = phase.field_error?.message.split(':')[0] ?? '';
  const guard = GUARD_COPY[token];
  return (
    <ErrorNotice
      kind={phase.problem}
      detail={guard ?? phase.field_error?.message ?? phase.detail}
      correlation_id={phase.correlation_id}
    />
  );
}
